/**
 * A 360° photo's six cube faces, made on the CPU (ADR-036, raised to 5760 px
 * on 30 Sep 2026; a package of its own since ADR-041). Pure maths, no three.js
 * and no DOM: the headset's pano worker samples a JPEG with it, and the media
 * worker samples the faces it encodes into the KTX2 cube with the same code,
 * so both make the same bytes.
 *
 * Source only, erasable syntax: Node strips the types, Vite reads it as is.
 */

/** The largest face: 1440² × 6 is 50 MB, what the budget leaves (worlds/budget.ts). */
export const PANO_FACE_MAX = 1440;

/**
 * A face's side for a photo `width` px wide: a third of the width (a multiple
 * of 16), at most 1440. At a face's centre a face spans 90°, so 1440 px is
 * 16 px a degree -- a 5760 px photo's own resolution; towards a face's edges
 * the cube is denser still. Narrower photos keep a third (3840 -> 1280, 14 px
 * a degree against the photo's 10.7): the second bilinear pass, face to
 * screen, then softens less, at no cost over the 5760 budget.
 */
export function cubeFace(width: number): number {
  return Math.min(PANO_FACE_MAX, Math.round(width / 3 / 16) * 16);
}

/** The largest face of the media worker's KTX2 cube (ADR-041): 2048² ASTC 6x6 is 2.8 MB a face. */
export const PANO_CUBE_FACE_MAX = 2048;

/**
 * The media worker's face side for a photo `width` px wide: a quarter of the
 * width (a multiple of 16), at most 2048 -- an 8192 px master's own 22.8 px a
 * degree at a face's centre --, and never smaller than the headset's JPEG path
 * would make (`cubeFace`), so the cube is never the softer of the two
 * (5760 -> 1440, 8192 -> 2048, 3840 -> 1280, 2048 -> 688).
 */
export function cubeFaceFor(width: number): number {
  return Math.max(Math.min(Math.round(width / 4 / 16) * 16, PANO_CUBE_FACE_MAX), cubeFace(width));
}

/**
 * The direction of a face's texel in WebGL's cube map convention, `a` and `b`
 * the texel's centre from −1 to 1 along its row and down its rows.
 */
export function faceDirection(face: number, a: number, b: number): [number, number, number] {
  switch (face) {
    case 0:
      return [1, -b, -a];
    case 1:
      return [-1, -b, a];
    case 2:
      return [a, 1, b];
    case 3:
      return [a, -1, -b];
    case 4:
      return [a, -b, 1];
    default:
      return [-a, -b, -1];
  }
}

/**
 * The photo's colour at pixel position (`x`, `y`) -- pixel centres at .5, top
 * row first --, bilinear, into `out` at `at`. Round the seam the columns wrap
 * (the column at ±180° mixes the last with the first), at the poles the rows
 * are held.
 */
export function sampleEquirect(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
  x: number,
  y: number,
  out: Uint8Array,
  at: number,
): void {
  const px = x - 0.5;
  const py = Math.min(Math.max(y - 0.5, 0), height - 1);
  const fx0 = Math.floor(px);
  const fy0 = Math.floor(py);
  const fx = px - fx0;
  const fy = py - fy0;
  const x0 = ((fx0 % width) + width) % width;
  const x1 = x0 + 1 === width ? 0 : x0 + 1;
  const y1 = fy0 + 1 < height ? fy0 + 1 : fy0;
  const r0 = fy0 * width;
  const r1 = y1 * width;
  const i00 = (r0 + x0) * 4;
  const i01 = (r0 + x1) * 4;
  const i10 = (r1 + x0) * 4;
  const i11 = (r1 + x1) * 4;
  const w00 = (1 - fx) * (1 - fy);
  const w01 = fx * (1 - fy);
  const w10 = (1 - fx) * fy;
  const w11 = fx * fy;
  for (let c = 0; c < 3; c++) {
    out[at + c] = Math.round(
      pixels[i00 + c]! * w00 +
        pixels[i01 + c]! * w01 +
        pixels[i10 + c]! * w10 +
        pixels[i11 + c]! * w11,
    );
  }
  out[at + 3] = 255;
}

const TWO_PI = Math.PI * 2;

/** Bilinear, as sampleEquirect, into a clamped array (assigning rounds); `py` already held. */
function sampleInto(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
  px: number,
  py: number,
  out: Uint8ClampedArray,
  o: number,
): void {
  const fx0 = Math.floor(px);
  const fy0 = Math.floor(py);
  const fx = px - fx0;
  const fy = py - fy0;
  const x0 = fx0 < 0 ? fx0 + width : fx0 >= width ? fx0 - width : fx0;
  const x1 = x0 + 1 === width ? 0 : x0 + 1;
  const y1 = fy0 < height - 1 ? fy0 + 1 : fy0;
  const i00 = (fy0 * width + x0) * 4;
  const i01 = (fy0 * width + x1) * 4;
  const i10 = (y1 * width + x0) * 4;
  const i11 = (y1 * width + x1) * 4;
  const w00 = (1 - fx) * (1 - fy);
  const w01 = fx * (1 - fy);
  const w10 = (1 - fx) * fy;
  const w11 = fx * fy;
  out[o] = pixels[i00]! * w00 + pixels[i01]! * w01 + pixels[i10]! * w10 + pixels[i11]! * w11;
  out[o + 1] =
    pixels[i00 + 1]! * w00 +
    pixels[i01 + 1]! * w01 +
    pixels[i10 + 1]! * w10 +
    pixels[i11 + 1]! * w11;
  out[o + 2] =
    pixels[i00 + 2]! * w00 +
    pixels[i01 + 2]! * w01 +
    pixels[i10 + 2]! * w10 +
    pixels[i11 + 2]! * w11;
  out[o + 3] = 255;
}

/**
 * The six faces of an equirectangular photo (RGBA, top row first), each
 * `size`² RGBA, for a three.js CubeTexture: a texel at WebGL direction g
 * shows the photo where three's equirectangular lookup would find world
 * direction (−g.x, g.y, g.z), since three mirrors x for a CubeTexture (its
 * `flipEnvMap`). The background then shows what three's own conversion
 * showed, turned by the same `panoRotation(yaw)`.
 *
 * 12 million texels at 5760 px, so the trigonometry is shared: the four side
 * faces have one photo row per texel (the same for all four) and one column
 * per face column; the bottom is the top mirrored. A quarter of the
 * arctangents a texel-by-texel lookup takes (measured: 700 -> 430 ms).
 */
export function panoFaces(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
  size: number,
): Uint8Array[] {
  const maxRow = height - 1;
  const hold = (py: number): number => (py < 0 ? 0 : py > maxRow ? maxRow : py);
  const coordinate = (texel: number): number => (2 * (texel + 0.5)) / size - 1;
  const column = (gx: number, gz: number): number =>
    (Math.atan2(gz, -gx) / TWO_PI + 0.5) * width - 0.5;
  const row = (gx: number, gy: number, gz: number): number =>
    (0.5 - Math.atan2(gy, Math.sqrt(gx * gx + gz * gz)) / Math.PI) * height - 0.5;

  // The sides: the row depends only on (a, b), the same on all four.
  const sideRows = new Float32Array(size * size);
  for (let r = 0; r < size; r++) {
    const b = coordinate(r);
    for (let c = 0; c < size; c++) sideRows[r * size + c] = hold(row(1, -b, coordinate(c)));
  }
  // The top: its column and row per texel; the bottom mirrors it.
  const topColumns = new Float32Array(size * size);
  const topRows = new Float32Array(size * size);
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const [gx, gy, gz] = faceDirection(2, coordinate(c), coordinate(r));
      topColumns[r * size + c] = column(gx, gz);
      topRows[r * size + c] = row(gx, gy, gz);
    }
  }

  const faces: Uint8Array[] = [];
  const columns = new Float32Array(size);
  for (let face = 0; face < 6; face++) {
    const buffer = new ArrayBuffer(size * size * 4);
    const out = new Uint8ClampedArray(buffer);
    if (face === 2 || face === 3) {
      for (let r = 0; r < size; r++) {
        // The bottom's texel (r, c) looks the same way round as the top's
        // (size − 1 − r, c), as far below the horizon as that is above it.
        const from = face === 2 ? r : size - 1 - r;
        for (let c = 0; c < size; c++) {
          const i = from * size + c;
          const py = face === 2 ? topRows[i]! : maxRow - topRows[i]!;
          sampleInto(pixels, width, height, topColumns[i]!, hold(py), out, (r * size + c) * 4);
        }
      }
    } else {
      for (let c = 0; c < size; c++) {
        const [gx, , gz] = faceDirection(face, coordinate(c), 0);
        columns[c] = column(gx, gz);
      }
      for (let r = 0; r < size; r++) {
        for (let c = 0; c < size; c++) {
          const i = r * size + c;
          sampleInto(pixels, width, height, columns[c]!, sideRows[i]!, out, i * 4);
        }
      }
    }
    faces.push(new Uint8Array(buffer));
  }
  return faces;
}
